import styled from '@emotion/styled'
import { TFunction } from 'i18next'
import { useEffect, useState } from 'react'

import {
  type DefectComment,
  fetchDefectCommentsByDefectIdAction,
} from '../../actions/defectComments'
import ArrowDown from '../../icons/ArrowDown'
import ArrowUp from '../../icons/ArrowUp'
import { useTypedDispatch, useTypedSelector } from '../../index'
import moment from '../../lib/moment-fi'
import { getColor, MaybeColor, theme } from '../../Theme'
import { Defect } from '../../types'
import TransparentButton from '../button/TransparentButton'
import { CommentFormArea } from '../comments/CommentFormArea'
import { CommentList, DefectTextRow } from '../comments/CommentList'
import { Status } from '../create-defect/types'
import Divider from '../divider/Divider'
import RestrictionIcon from '../findings/RestrictionIcon'
import RestrictionText from '../findings/RestrictionText'
import Text from '../Text'

interface BoldProps {
  width: string
}

const Bold = styled.div<BoldProps>`
  ${theme.text('normal')};
  font-weight: bold;
  width: ${(p) => p.width};
  display: flex;
  word-break: break-word;
  padding-left: 1px;
  line-height: 1;
`

interface IconContainerProps {
  justifyContent: string
}

enum DefectStatus {
  ACCEPTED = 'ACCEPTED',
  ASSIGNED = 'ASSIGNED',
  IN_PROCESS = 'IN_PROCESS',
  REGISTERED = 'REGISTERED',
  SOLVED = 'SOLVED',
  TRANSFERRED = 'TRANSFERRED',
}

const IconContainer = styled.div<IconContainerProps>`
  display: flex;
  width: 10%;
  justify-content: ${(p) => p.justifyContent};
  align-items: center;
  padding-left: 8px;
  padding-right: 8px;
  margin-left: 4px;
`

const DefectHeaderContainer = styled.div`
  ${theme.layout.flexRow};
  width: 80%;
  margin-left: 10px;
`

const DefectHeaderTextContainer = styled.div`
  width: 68%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

interface DefectRowBaseContainerProps {
  open: boolean
}

const DefectRowBaseContainer = styled.div<DefectRowBaseContainerProps>`
  background: ${(p) =>
    getColor(p.theme, [p.color as MaybeColor, 'white'], [p.color as MaybeColor, 'grayDark'])};
  width: 100%;
  transition: all 0.3s ease-out;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-shadow: 0px 2px 6px 1px rgba(0, 0, 0, 0.1);
  gap: 10px;
`

const DefectRowContainer = styled.div`
  width: 97%;
  background: ${(p) =>
    getColor(p.theme, [p.color as MaybeColor, 'white'], [p.color as MaybeColor, 'grayDark'])};
  ${theme.layout.flexRow};
  padding: 8px;
  align-content: center;
  flex-basis: 100%;
`

const DefectAdditionalDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 20px;
  padding-bottom: 10px;
  gap: 8px;
  width: 85%;
`

const IssuedTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 5px;
  width: 45px;
`

const CommentAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

const Toast = styled.div<{ type: 'success' | 'error' }>`
  ${theme.text('small', 'decorative', 'bold')};
  background: ${(p) =>
    p.type === 'success'
      ? getColor(p.theme, ['subtleGreen', 'white'], ['darkGreen', 'grayDark'])
      : getColor(p.theme, ['red', 'white'], ['red', 'grayDark'])};
  padding: ${theme.spacing.sizes.small};
  width: 100%;
  text-align: center;
  max-width: ${theme.maxWidths.column};
  border-radius: ${theme.borderRadius.button};
`

const EMPTY_COMMENTS: DefectComment[] = []

type Props = {
  t: TFunction
  defect: Defect
  nightMode: boolean
  status: Status
  open?: boolean
}

export const formatIssuedTime = (issuedTime: string): { dayMonth: string; year: string } => {
  const fallback = { dayMonth: '-', year: '' }
  try {
    if (!issuedTime) return fallback
    const date = moment(issuedTime)
    return {
      dayMonth: date.format('D.M.'),
      year: date.format('YYYY'),
    }
  } catch (e) {
    return fallback
  }
}

export const formatDescription = (description: string): string => {
  try {
    const parts = description.split('|')
    return parts.length > 2 ? `${parts[1]} - ${parts[2]}` : '-'
  } catch (e) {
    return '-'
  }
}

export const formatDefectCodeDescription = (description: string): string | undefined => {
  try {
    const parts = description?.split('|')
    if (parts?.length > 0) {
      return parts.reduce<string | undefined>((prev, cur) => {
        if (!prev) return cur
        return cur?.length ? `${prev} > ${cur}` : prev
      }, undefined)
    }
  } catch (e) {
    return '-'
  }
}

export const formatDateTime = (completionTime: string): string => {
  try {
    return completionTime ? moment(completionTime).format('D.M.Y H:mm') : ''
  } catch (e) {
    return ''
  }
}

export const getStatusTranslation = (status: string, t: TFunction): string =>
  status in DefectStatus ? t(`defect.status.${status}`) : status

const formatStatusTimestamp = (defect: Defect): string => {
  switch (defect.status) {
    case DefectStatus.REGISTERED:
      return formatDateTime(defect.issuedTime)
    default:
      return formatDateTime(defect.repairCompletionTime)
  }
}

export const DefectAdditionalDetails = ({ defect, t, nightMode, open }: Omit<Props, 'status'>) => {
  const textColor = nightMode ? 'grayPink' : 'grayDark'
  const [addCommentOpen, setAddCommentOpen] = useState<boolean>(false)
  const [successToastVisible, setSuccessToastVisible] = useState<boolean>(false)
  const [errorToastVisible, setErrorToastVisible] = useState<boolean>(false)
  const dispatch = useTypedDispatch()

  useEffect(() => {
    dispatch(fetchDefectCommentsByDefectIdAction(defect.id))
  }, [defect.id, dispatch])

  const comments = useTypedSelector(
    (state) => state.defectComments.commentsByDefectId[defect.id] ?? EMPTY_COMMENTS
  )

  const actualRepairAction = defect.actualRepairAction
  const repairComment = defect.repairComment

  return (
    <DefectAdditionalDetailsContainer>
      <Text color={textColor}>{formatDefectCodeDescription(defect.defectCodeDescription)}</Text>
      {!open ? (
        <>
          <Bold width="90%">{defect.comments?.[0]?.comment}</Bold>
          <CommentList comments={comments} successToastVisible={successToastVisible} />
          <Divider />
          <Bold width="100%">
            {getStatusTranslation(defect.status, t)} {formatStatusTimestamp(defect)}
          </Bold>
          {actualRepairAction && <Text>{actualRepairAction}</Text>}
          {repairComment && <DefectTextRow>{repairComment}</DefectTextRow>}
        </>
      ) : (
        <>
          <Bold width="90%">{defect.comments?.[0]?.comment}</Bold>
          <CommentList comments={comments} successToastVisible={successToastVisible} />
          <Text color={textColor}>
            {getStatusTranslation(defect.status, t)} {formatStatusTimestamp(defect)}
          </Text>
        </>
      )}
      <CommentAreaContainer>
        {errorToastVisible && <Toast type="error">{t('defect.comment.create.error')}</Toast>}
        {addCommentOpen && (
          <>
            <CommentFormArea
              defectId={defect.id}
              setErrorToastVisible={setErrorToastVisible}
              setSuccessToastVisible={setSuccessToastVisible}
            />
          </>
        )}
        {open && (
          <TransparentButton onClick={() => setAddCommentOpen(true)} disabled={!open}>
            {t('defect.comment.add')}
          </TransparentButton>
        )}
      </CommentAreaContainer>
    </DefectAdditionalDetailsContainer>
  )
}

const DefectsRow = ({ defect, t, nightMode, status, open: defectOpen }: Props): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false)

  const issuedTime = formatIssuedTime(defect.issuedTime)

  return (
    <DefectRowBaseContainer open={open}>
      <DefectRowContainer onClick={() => setOpen(!open)}>
        <IconContainer justifyContent="flex-start">
          <RestrictionIcon
            criticality={defect.criticality}
            usageCode={defect.restrictionCodeDescription}
            nightMode={nightMode}
            status={status}
          />
        </IconContainer>
        <DefectHeaderContainer>
          <IssuedTimeContainer>
            <Text>{issuedTime.dayMonth}</Text>
            <Text>{issuedTime.year}</Text>
          </IssuedTimeContainer>
          <DefectHeaderTextContainer>
            <Bold width="100%">{formatDescription(defect.defectDescription)}</Bold>
            {defect.restrictionCodeDescription && (
              <RestrictionText width={'100%'} paddingLeft="1px" lineHeight="1">
                {t('restrictionInfos')}: {defect.restrictionCodeDescription}
              </RestrictionText>
            )}
          </DefectHeaderTextContainer>
        </DefectHeaderContainer>
        <IconContainer justifyContent="flex-end">
          {open ? (
            <ArrowUp
              iconSize="normal"
              stroke={getColor({ nightMode }, ['mediumGreen'], ['darkGreen'])}
            />
          ) : (
            <ArrowDown
              iconSize="normal"
              stroke={getColor({ nightMode }, ['mediumGreen'], ['darkGreen'])}
            />
          )}
        </IconContainer>
      </DefectRowContainer>
      {open && (
        <DefectAdditionalDetails defect={defect} t={t} nightMode={nightMode} open={defectOpen} />
      )}
    </DefectRowBaseContainer>
  )
}

export default DefectsRow
